import React from "react";
import { Popover } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

export type HeaderMenuItem = {
  label: string;
  href?: string;
  action?: () => void;
  icon?: React.ReactNode;
  showWhenLoggedIn?: boolean;
  showWhenLoggedOut?: boolean;
  external?: boolean;
  highlight?: boolean;
  hideMeFilter?: boolean;
  children?: HeaderMenuItem[];
};

interface ProfilePopupProps {
  id: string | undefined;
  open: boolean;
  anchorEl: null | HTMLElement;
  handleClose: () => void;
  actions: HeaderMenuItem[];
  handleLogout: () => void;
  logoutLoading: boolean;
  user: unknown;
}

const ProfilePopup: React.FC<ProfilePopupProps> = ({
  id,
  open,
  anchorEl,
  handleClose,
  actions,
  handleLogout,
  logoutLoading,
  user,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  /**
   * Render a single menu item (link or button), ensuring icon sizing.
   */
  const renderMenuItem = (item: HeaderMenuItem, key: string | number) => {
    if (item.hideMeFilter) {
      return null;
    }
    let iconNode: React.ReactNode = null;
    if (item.icon && React.isValidElement(item.icon)) {
      if (item.icon.type === "img") {
        iconNode = React.cloneElement(item.icon, {
          ...item.icon.props,
          className: item.icon.props && item.icon.props.className
            ? item.icon.props.className
            : "max-w-[16px] max-h-[16px]",
        });
      } else {
        iconNode = item.icon;
      }
    }
    if (item.href) {
      if (item.external) {
        return (
          <a
            key={key}
            href={item.href}
            target="_blank"
            rel="noopener noreferrer"
            onClick={handleClose}
            className="popover-menu-item !mx-[8px] flex max-w-full cursor-pointer flex-row flex-nowrap items-center gap-[8px] !no-underline custom-text-[poppins-400-16px]"
          >
            {iconNode}
            <span>{t(item.label)}</span>
          </a>
        );
      }
      return (
        <button
          key={key}
          type="button"
          onClick={() => {
            handleClose();
            navigate(item.href || "/");
          }}
          className="popover-menu-item !mx-[8px] flex max-w-full cursor-pointer flex-row flex-nowrap items-center gap-[8px] !no-underline custom-text-[poppins-400-16px]"
        >
          {iconNode}
          <span>{t(item.label)}</span>
        </button>
      );
    }
    if (item.label.toLowerCase() === "logout") {
      return (
        <button
          key={key}
          type="button"
          onClick={() => {
            handleLogout();
            handleClose();
          }}
          className={
            "popover-menu-item !mx-[8px] flex max-w-full cursor-pointer flex-row flex-nowrap items-center gap-[8px] !no-underline custom-text-[poppins-400-16px] text-red"
          }
          disabled={logoutLoading}
        >
          {logoutLoading ? (
            <span className="custom-text-[poppins-400-14px]">...</span>
          ) : (
            <>
              {iconNode}
              <span>{t(item.label)}</span>
            </>
          )}
        </button>
      );
    }
    if (item.action) {
      return (
        <button
          key={key}
          type="button"
          onClick={() => {
            item.action && item.action();
            handleClose();
          }}
          className={
            item.highlight
              ? "popover-menu-item !mx-[8px] flex max-w-full cursor-pointer flex-row flex-nowrap items-center gap-[8px] !no-underline custom-text-[poppins-400-16px] text-red"
              : "popover-menu-item !mx-[8px] flex max-w-full cursor-pointer flex-row flex-nowrap items-center gap-[8px] !no-underline custom-text-[poppins-400-16px]"
          }
          disabled={item.label.toLowerCase() === "logout" && logoutLoading}
        >
          {item.label.toLowerCase() === "logout" && logoutLoading ? (
            <span className="custom-text-[poppins-400-16px]">...</span>
          ) : (
            <>
              {iconNode}
              <span>{t(item.label)}</span>
            </>
          )}
        </button>
      );
    }
    return null;
  };

  if (!open) {
    return null;
  }

  const shouldShowDivider = (items: HeaderMenuItem[], logoutIndex: number): boolean => {
    return items.slice(0, logoutIndex).some(item => !item.hideMeFilter);
  };

  return (
    <Popover
      id={id}
      open={open}
      anchorEl={anchorEl}
      onClose={handleClose}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "left",
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "left",
      }}
      slotProps={{
        paper: {
          sx: {
            p: "21px 18px",
            borderRadius: "20px",
            boxShadow: "0px 7px 29px rgba(100, 100, 111, 0.2)",
            maxWidth: "226px",
            width: "100%",
            display: "flex",
            flexDirection: "column",
            gap: "16px",
          },
        },
      }}
    >
      {actions && actions.length > 0 ? (
        actions.map((item, idx) => {
          if (item.label.toLowerCase() === "logout") {
            return (
              <React.Fragment key={item.label}>
                {shouldShowDivider(actions, idx) && (
                  <hr className="h-[1px] border-black bg-black pr-[3px] opacity-30" />
                )}
                {renderMenuItem(item, idx)}
              </React.Fragment>
            );
          }
          return renderMenuItem(item, idx);
        })
      ) : (
        <span className="custom-text-[poppins-400-14px] text-gray-400">No options</span>
      )}
    </Popover>
  );
};

export default ProfilePopup;
